// @ts-nocheck
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ArrowRight, Download, Search, Sparkles, Upload, Zap } from 'lucide-react';
import { c } from './ui';

const NavSearch = forwardRef(function NavSearch({ jump, goTo, canImport, laya, onAddFast, onToggleFast, onImport, onExport }, ref) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
      inputRef.current?.select();
      setOpen(true);
    },
  }));

  const rows = useMemo(() => {
    const go = jump.map((n) => ({ key: `go-${n.id}`, group: 'Jump to a section', label: n.label, icon: ArrowRight, run: () => goTo(n.id) }));
    const actions = [
      { key: 'add-fast', group: 'Actions', label: 'Add a fast answer', icon: Sparkles, run: onAddFast },
      { key: 'toggle-fast', group: 'Actions', label: laya.enabled ? 'Pause fast answers' : 'Resume fast answers', icon: Zap, run: onToggleFast },
      ...(onExport ? [{ key: 'export', group: 'Actions', label: 'Export agent JSON', icon: Download, run: onExport }] : []),
      ...(canImport ? [{ key: 'import', group: 'Actions', label: 'Import agent JSON', icon: Upload, run: onImport }] : []),
    ];
    const needle = q.trim().toLowerCase();
    const all = [...go, ...actions];
    return needle ? all.filter((r) => r.label.toLowerCase().includes(needle)) : all;
  }, [q, jump, laya.enabled, canImport, onExport]);

  useEffect(() => setSel(0), [q]);


  useEffect(() => {
    if (!open) return;
    const onDown = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const run = (row) => {
    if (!row) return;
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
    row.run();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setSel((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(rows[sel]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQ('');
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    boxRef.current?.querySelector('.ags-on')?.scrollIntoView({ block: 'nearest' });
  }, [sel, open]);

  let lastGroup = '';
  return (
    <div className={c('nsearch')} ref={boxRef}>
      <label className={c('nsi')}>
        <Search className="ags-i" />
        <input
          ref={inputRef}
          value={q}
          placeholder="Search sections, actions"
          aria-label="Jump to a section or run an action"
          role="combobox"
          aria-expanded={open}
          autoComplete="off"
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <span className={c('kbd')}>Ctrl K</span>
      </label>
      {open && (
        <div className={c('nres')} role="listbox">
          {rows.length === 0 && <div className={c('none')}>Nothing matches “{q}”.</div>}
          {rows.map((r, i) => {
            const head = r.group !== lastGroup ? r.group : '';
            lastGroup = r.group;
            return (
              <div key={r.key}>
                {head && <h5>{head}</h5>}
                <button type="button" role="option" aria-selected={i === sel} className={c(i === sel && 'on')} onMouseEnter={() => setSel(i)} onClick={() => run(r)}>
                  <r.icon className="ags-i" />
                  {r.label}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default NavSearch;
